import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../lib/auth-context.js";
import { api, ApiError, type TwoFactorStatus } from "../lib/api.js";

interface SettingRow {
  key: string;
  label: string;
  type: "text" | "number";
  value: string;
  isDefault: boolean;
}

export function SettingsPage() {
  const { user } = useAuth();
  const readOnly = user?.role !== "admin";
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    fetch("/api/settings", { credentials: "include" })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: { values: SettingRow[] }) => setRows(d.values))
      .catch((e: Error) => setError(e.message));
  }, []);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    setOk(false);
    const entries = rows.map((r) => ({ key: r.key, value: r.value }));
    const res = await fetch("/api/settings", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }),
    });
    setSaving(false);
    if (res.ok) {
      setOk(true);
      setTimeout(() => setOk(false), 2000);
    } else setError("Erro ao salvar: " + res.status);
  }

  function update(key: string, value: string) {
    setRows((r) =>
      r.map((x) => (x.key === key ? { ...x, value, isDefault: false } : x)),
    );
  }

  // Subtítulo depende do contexto do usuário. Para admin, número de campos
  // (é o que ele vem editar); para viewer, o aviso de leitura, colado ao
  // sub para não brigar por espaço com o title.
  const sub = readOnly
    ? "Somente administradores podem editar. Estas são as configurações vigentes."
    : `${rows.length} ${rows.length === 1 ? "campo" : "campos"} de configuração`;

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Configurações</span>
          <h1>Ajustes</h1>
          <div className="nx-page-head__sub">{sub}</div>
        </div>
      </div>
      {error && <div className="nx-error">{error}</div>}
      <form
        onSubmit={save}
        className="nx-card"
        style={{ marginTop: "var(--nx-space-4)" }}
      >
        {rows.map((r) => (
          <label key={r.key} className="nx-field">
            <span>
              {r.label} <small className="mono">({r.key})</small>{" "}
              {r.isDefault && (
                <span
                  className="nx-badge nx-badge--neutral"
                  style={{ marginLeft: 4 }}
                >
                  default
                </span>
              )}
            </span>
            <input
              type={r.type === "number" ? "number" : "text"}
              value={r.value}
              onChange={(e) => update(r.key, e.target.value)}
              disabled={readOnly}
            />
          </label>
        ))}
        {!readOnly && (
          <div style={{ marginTop: "var(--nx-space-4)" }}>
            <button type="submit" className="nx-btn" disabled={saving}>
              {saving ? "Salvando…" : "Salvar alterações"}
            </button>
            {ok && (
              <span style={{ marginLeft: 12, color: "var(--nx-success)" }}>
                ✓ Salvo
              </span>
            )}
          </div>
        )}
      </form>

      <h2 style={{ marginTop: "var(--nx-space-8)" }}>Segurança da conta</h2>
      <TwoFactorSection />
    </div>
  );
}

/* ================================================================
 * 2FA: card na página de configurações + wizard de ativação
 * ================================================================ */

function TwoFactorSection() {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);

  const load = () => {
    api.twoFactor
      .status()
      .then(setStatus)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  };
  useEffect(load, []);

  return (
    <div className="nx-card" style={{ marginTop: "var(--nx-space-4)" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--nx-space-4)",
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Autenticação em duas etapas</h3>
          <p style={{ color: "var(--nx-mute)", margin: "4px 0 0 0" }}>
            Exige um código temporário (app authenticator) além da senha em cada
            novo login. Reduz risco de acesso indevido em caso de senha vazada.
          </p>
        </div>
        {status?.enabled ? (
          <span className="nx-badge nx-badge--available">Ativado</span>
        ) : (
          <span className="nx-badge nx-badge--neutral">Desativado</span>
        )}
      </div>

      {error && <div className="nx-error">{error}</div>}

      {status?.enabled ? (
        <div style={{ marginTop: "var(--nx-space-4)" }}>
          <p style={{ margin: 0, color: "var(--nx-mute-strong)" }}>
            Ativado em{" "}
            <b>
              {status.confirmedAt
                ? new Date(status.confirmedAt).toLocaleString("pt-BR")
                : "—"}
            </b>{" "}
            · {status.backupCodesRemaining} código(s) de recuperação restante(s)
          </p>
          <div
            style={{
              marginTop: "var(--nx-space-4)",
              display: "flex",
              gap: "var(--nx-space-2)",
              flexWrap: "wrap",
            }}
          >
            <button
              className="nx-btn nx-btn--ghost"
              onClick={() => setBackupOpen(true)}
            >
              Regenerar códigos de backup
            </button>
            <button
              className="nx-btn nx-btn--danger"
              onClick={() => setDisableOpen(true)}
            >
              Desativar 2FA
            </button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: "var(--nx-space-4)" }}>
          <button className="nx-btn" onClick={() => setWizardOpen(true)}>
            Ativar autenticação em duas etapas
          </button>
        </div>
      )}

      {wizardOpen && (
        <EnableWizard
          onClose={() => {
            setWizardOpen(false);
            load();
          }}
        />
      )}
      {disableOpen && (
        <DisableDialog
          onClose={() => {
            setDisableOpen(false);
            load();
          }}
        />
      )}
      {backupOpen && (
        <RegenerateBackupDialog
          onClose={() => {
            setBackupOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Wizard de ativação ---------------- */

interface WizardProps {
  onClose: () => void;
}

function EnableWizard({ onClose }: WizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(
    null,
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.twoFactor
      .setup()
      .then((s) => setSetup({ secret: s.secret, qrSvg: s.qrSvg }))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : "Erro ao iniciar 2FA"),
      );
  }, []);

  async function submitVerify(e: FormEvent) {
    e.preventDefault();
    if (!setup) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.twoFactor.verify(setup.secret, code);
      setBackupCodes(r.backupCodes);
      setStep(3);
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_code") {
        setError("Código incorreto. Confira o horário do dispositivo.");
      } else {
        setError("Erro ao verificar código.");
      }
    } finally {
      setLoading(false);
    }
  }

  function downloadBackup() {
    const blob = new Blob(
      [
        `nxvolt — códigos de recuperação 2FA\n`,
        `Gerados em: ${new Date().toISOString()}\n`,
        `\nCada código pode ser usado UMA vez.\n\n`,
        ...backupCodes.map((c) => `${c}\n`),
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "nxvolt-backup-codes.txt";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="nx-modal" role="dialog" aria-modal="true">
      <div className="nx-modal__card">
        <div className="nx-modal__head">
          <h2>Ativar autenticação em duas etapas</h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <div className="nx-modal__body">
          <div className="nx-page-head__eyebrow">Etapa {step} de 3</div>

          {step === 1 && (
            <div>
              <p>
                Escaneie o QR code abaixo em um app authenticator (Google
                Authenticator, Authy, 1Password, Microsoft Authenticator etc.).
              </p>
              {setup ? (
                <div
                  style={{
                    display: "flex",
                    gap: "var(--nx-space-4)",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      background: "var(--nx-neutral-0)",
                      padding: 8,
                      borderRadius: "var(--nx-radius-md)",
                    }}
                    dangerouslySetInnerHTML={{ __html: setup.qrSvg }}
                  />
                  <div>
                    <p style={{ margin: "0 0 4px 0", color: "var(--nx-mute)" }}>
                      Ou digite manualmente:
                    </p>
                    <code
                      className="mono"
                      style={{
                        fontSize: "var(--nx-text-md)",
                        background: "var(--nx-bg-alt)",
                        padding: "6px 10px",
                        borderRadius: "var(--nx-radius-sm)",
                        userSelect: "all",
                        wordBreak: "break-all",
                        display: "inline-block",
                      }}
                    >
                      {setup.secret}
                    </code>
                  </div>
                </div>
              ) : (
                <p style={{ color: "var(--nx-mute)" }}>Gerando QR code…</p>
              )}
              {error && <div className="nx-error">{error}</div>}
            </div>
          )}

          {step === 2 && setup && (
            <form onSubmit={submitVerify}>
              <p>Digite o código de 6 dígitos exibido no app.</p>
              <label className="nx-field">
                <span>Código</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  autoFocus
                  className="mono"
                  style={{ letterSpacing: "0.3em", fontSize: "1.25rem" }}
                />
              </label>
              {error && <div className="nx-error">{error}</div>}
            </form>
          )}

          {step === 3 && (
            <div>
              <p>
                <b>Guarde estes códigos de recuperação em local seguro.</b> Cada
                código pode ser usado uma única vez para entrar caso você perca
                o app authenticator.
              </p>
              <div className="nx-code-grid">
                {backupCodes.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>
              <div style={{ marginTop: "var(--nx-space-3)" }}>
                <button
                  className="nx-btn nx-btn--ghost"
                  onClick={downloadBackup}
                >
                  Baixar como .txt
                </button>
              </div>
              <label
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginTop: "var(--nx-space-4)",
                }}
              >
                <input
                  type="checkbox"
                  checked={savedConfirmed}
                  onChange={(e) => setSavedConfirmed(e.target.checked)}
                />
                <span>Guardei os códigos em um local seguro.</span>
              </label>
            </div>
          )}
        </div>
        <div className="nx-modal__foot">
          {step === 1 && (
            <>
              <button className="nx-btn nx-btn--link" onClick={onClose}>
                Cancelar
              </button>
              <button
                className="nx-btn"
                onClick={() => setStep(2)}
                disabled={!setup}
              >
                Próximo
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <button
                className="nx-btn nx-btn--link"
                onClick={() => setStep(1)}
              >
                Voltar
              </button>
              <button
                className="nx-btn"
                onClick={submitVerify}
                disabled={loading || code.length !== 6}
              >
                {loading ? "Verificando…" : "Verificar e ativar"}
              </button>
            </>
          )}
          {step === 3 && (
            <button
              className="nx-btn"
              disabled={!savedConfirmed}
              onClick={onClose}
            >
              Concluir
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Diálogo de desativação ---------------- */

function DisableDialog({ onClose }: WizardProps) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.twoFactor.disable(password, code);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError("Senha incorreta.");
        else if (err.code === "invalid_code") setError("Código inválido.");
        else setError("Erro ao desativar 2FA.");
      } else setError("Erro de rede.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="nx-modal" role="dialog" aria-modal="true">
      <div className="nx-modal__card" style={{ maxWidth: 480 }}>
        <div className="nx-modal__head">
          <h2>Desativar 2FA</h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="nx-modal__body">
            <p>
              Confirme sua senha e um código atual (do app ou de recuperação)
              para desligar a autenticação em duas etapas.
            </p>
            <label className="nx-field">
              <span>Senha atual</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <label className="nx-field">
              <span>Código (6 dígitos) ou código de recuperação</span>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                className="mono"
              />
            </label>
            {error && <div className="nx-error">{error}</div>}
          </div>
          <div className="nx-modal__foot">
            <button
              type="button"
              className="nx-btn nx-btn--link"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="nx-btn nx-btn--danger"
              disabled={loading}
            >
              {loading ? "Desativando…" : "Desativar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Regenerar códigos de backup ---------------- */

function RegenerateBackupDialog({ onClose }: WizardProps) {
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const r = await api.twoFactor.regenerateBackupCodes(password, code);
      setNewCodes(r.backupCodes);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError("Senha incorreta.");
        else if (err.code === "invalid_code") setError("Código inválido.");
        else setError("Erro ao regenerar códigos.");
      } else setError("Erro de rede.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="nx-modal" role="dialog" aria-modal="true">
      <div className="nx-modal__card" style={{ maxWidth: 520 }}>
        <div className="nx-modal__head">
          <h2>Regenerar códigos de recuperação</h2>
          <button
            className="nx-modal__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
        {newCodes ? (
          <>
            <div className="nx-modal__body">
              <p>
                <b>Novos códigos gerados.</b> Os anteriores foram invalidados.
                Guarde em local seguro.
              </p>
              <div className="nx-code-grid">
                {newCodes.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>
            </div>
            <div className="nx-modal__foot">
              <button className="nx-btn" onClick={onClose}>
                Concluir
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="nx-modal__body">
              <p>
                Confirme com sua senha + um código atual (do app ou de
                recuperação). Após a regeneração, os códigos antigos param de
                funcionar.
              </p>
              <label className="nx-field">
                <span>Senha atual</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>
              <label className="nx-field">
                <span>Código</span>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  className="mono"
                />
              </label>
              {error && <div className="nx-error">{error}</div>}
            </div>
            <div className="nx-modal__foot">
              <button
                type="button"
                className="nx-btn nx-btn--link"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button type="submit" className="nx-btn" disabled={loading}>
                {loading ? "Regenerando…" : "Regenerar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
